import { ReactNode, useEffect, useState } from 'react';

import { PlatformLocalUser, usePlatformUpdateCurrentUser } from '@cloudquery/api-platform';

import { PlatformPermissions } from '@/constants/platformPermissions';
import { useAllowedPlatformFeatures } from '@/hooks/useAllowedPlatformFeatures';

import { useHasTeamsPermissions } from '@/hooks/useHasTeamsPermissions';

import { TrackingConsentContext } from './context';
import { useAuth } from '../auth';
import { TrackingConsentPrompt } from './components';

interface TrackingConsentProviderInnerProps {
  children: ReactNode;
  refetchUser: () => Promise<void>;
  user: PlatformLocalUser;
}

const TrackingConsentProviderInner = ({
  children,
  refetchUser,
  user,
}: TrackingConsentProviderInnerProps) => {
  const [promptOpen, setPromptOpen] = useState(false);

  const { mutateAsync: updateCurrentUser } = usePlatformUpdateCurrentUser();

  useEffect(() => {
    const consentWasNotOffered =
      user.tracking_opted_in === null || user.tracking_opted_in === undefined;

    if (consentWasNotOffered) {
      setPromptOpen(true);
    }
  }, [user]);

  const updateConsentValue = async (consentValue: boolean) => {
    await updateCurrentUser(
      {
        data: {
          tracking_opt_in: consentValue,
        },
      },
      { onSuccess: refetchUser },
    )
      .then(() => setPromptOpen(false))
      .catch(() => null);
  };

  return (
    <TrackingConsentContext.Provider value={{ trackingOptedIn: user.tracking_opted_in !== false }}>
      {children}
      {promptOpen && (
        <TrackingConsentPrompt onAccepted={() => updateConsentValue(true)} open={promptOpen} />
      )}
    </TrackingConsentContext.Provider>
  );
};

interface TrackingConsentProviderProps {
  children: ReactNode;
}

export const TrackingConsentProvider = ({ children }: TrackingConsentProviderProps) => {
  const { refetch, user } = useAuth();
  const { readonlyUserEditDisabled } = useAllowedPlatformFeatures();

  const isAdmin = useHasTeamsPermissions([PlatformPermissions.ADMIN_READ]);
  const canWrite = useHasTeamsPermissions([PlatformPermissions.GENERAL_WRITE]);

  const isConsentDisabled = readonlyUserEditDisabled && !isAdmin && !canWrite;

  return user && !isConsentDisabled ? (
    <TrackingConsentProviderInner refetchUser={refetch} user={user}>
      {children}
    </TrackingConsentProviderInner>
  ) : (
    <>{children}</>
  );
};
